import React, { useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import Script from 'next/script';
import { throttle } from 'lodash-es';
import { useRouter } from 'next/router';
import classNames from 'classnames';

import { serverSideTranslation } from '../util/next';
import { getIRPageData, IInvestorRelationData, EInvestorSectionKey } from '../api/investorApi';
import NavAnchor, { AnchorProvider } from '../components/NavAnchor';
import Page from '../components/Layout/Page';
import ScrollPosition, { IScrollTopContext } from '../components/ScrollPosition';
import { useScreenSize, BreakPoint } from '../components/ScreenSize';

import {
  Governance,
  Announcement,
  Result,
  Report,
  CsrReport,
  Calendar,
  ConferenceCalls,
  AnalystCoverage,
  StockInfo,
  Contacts,
} from '../modules/Investor';
import { getPageContent } from '../api/pagesApi';
import { genShareConfig } from '../components/Share/use-config-share';
import VideoTopBanner from '../modules/VideoTopBanner';

const sectionMap: { [key: string]: (props: any) => JSX.Element } = {
  [EInvestorSectionKey.CorporateGovernance]: Governance,
  [EInvestorSectionKey.Announcements]: Announcement,
  [EInvestorSectionKey.QuarterlyResults]: Result,
  [EInvestorSectionKey.FinancialReports]: Report,
  [EInvestorSectionKey.CsrReports]: CsrReport,
  [EInvestorSectionKey.EventCalendar]: Calendar,
  [EInvestorSectionKey.ConferenceCalls]: ConferenceCalls,
  [EInvestorSectionKey.AnalystCoverage]: AnalystCoverage,
  [EInvestorSectionKey.StockInformation]: StockInfo,
  [EInvestorSectionKey.ContactUs]: Contacts,
};

interface IProps {
  pageData: IInvestorRelationData;
  pageContentData: Record<string, any>;
}

function InvestorRelation(props: IProps) {
  const { pageData, pageContentData } = props;
  const [isStick, setIsStick] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  const { shareContent = {}, pageContent } = pageContentData || {};
  const { videoTitle, videoPoster, videoTitleStyle, h5VideoPoster } = pageContent;

  const { screen } = useScreenSize();
  const isMobile = screen === BreakPoint.md || screen === BreakPoint.sm;

  const { locale } = useRouter();

  const { t } = useTranslation('investor');
  const sectionList = t('sectionList', { returnObjects: true }) as any[];
  const stockInfoLang = t('langStockInfo');
  // const title = t('titleIR');
  // const description =
  //   '美团企业官网，美团官方信息发布平台，美团香港交易所上市信息，最新估值、市值、股票、股权、融资、投资、收购、并购信息发布平台，美团年报，招股书，招股文件下载官方平台。美团是中国领先的生活服务电子商务平台，用科技连接消费者和商家，提供服务以满足人们日常「吃」的需求，并进一步扩展至多种生活和旅游服务。美团的使命是“帮大家吃得更好，生活更好”。';

  const handleStickScroll = (position: IScrollTopContext) => {
    const { scrollTop: top } = position;
    /**
     * [BreakPoint.lg, BreakPoint.xl] -> 400 - 60
     * [BreakPoint.xl, Infinity] -> 400 - 80
     * [0, BreakPoint.lg] -> 160 - 60
     * offset: 10
     */
    switch (screen) {
      case BreakPoint.lg:
        setIsStick(top >= 340);
        break;
      case BreakPoint.xl:
      case BreakPoint['2xl']:
        setIsStick(top >= 320);
        break;
      default:
        setIsStick(top >= 260);
    }
  };

  const handleNavTop = useMemo(
    () =>
      throttle(
        (position: IScrollTopContext) => {
          const { scrollTop: top } = position;
          setScrollTop(top);
        },
        isMobile ? 20 : 30,
      ),
    [isMobile],
  );

  const handleScroll = (position: IScrollTopContext) => {
    handleStickScroll(position);
    handleNavTop(position);
  };

  const wrapperCls = useMemo(() => {
    return classNames(
      {
        'overflow-x-hidden': isMobile, // 保证positon: sticky生效，只有移动端才添加hidden
      },
      `w-full bg-[#F7F8F9] px-0 lg:pt-[68px] `,
    );
  }, [isMobile]);

  const containerCls = useMemo(() => {
    return `my-0 mx-auto w-full px-0 lg:px-5 max-w-screen-xl flex flex-col lg:flex-row`;
  }, []);

  const navWrapperCls = useMemo(() => {
    return classNames('w-full lg:w-[190px] lg:mr-[20px] relative', { 'h-[42px]': isStick && isMobile });
  }, [isStick, isMobile]);

  const navStickyWrapperCls = useMemo(() => {
    return classNames({
      'fixed sticky lg:top-[110px] xl:top-[130px] pb-[20px]': isStick && !isMobile,
      'fixed top-[60px] bg-[#fff] w-full z-[10001] box-s': isStick && isMobile,
    });
  }, [isStick, isMobile]);

  const contentCls = useMemo(() => {
    return 'flex-1 px-5 lg:px-0 lg:mt-[-68px]';
  }, []);

  const videoCoverImg = useMemo(() => {
    return isMobile ? h5VideoPoster : videoPoster;
  }, [isMobile, h5VideoPoster, videoPoster]);

  return (
    <>
      <Script
        id="easyXDM"
        src="https://s3.meituan.net/static-prod01/com.sankuai.fspfecap.officeweb-files/easyXDM-2.4.19.3.min.js"
        strategy="beforeInteractive"
      />

      <AnchorProvider defaultAnchor={sectionList[0].anchor || ''} scrollTop={scrollTop}>
        <ScrollPosition onScroll={handleScroll}>
          <Page title={shareContent.shareTitle}>
            <VideoTopBanner videoTitleStyle={videoTitleStyle} videoTitle={videoTitle} videoCoverImg={videoCoverImg} />

            <div className={wrapperCls}>
              <div className={containerCls}>
                <div className={navWrapperCls}>
                  <div className={navStickyWrapperCls}>
                    <NavAnchor content={sectionList} />
                  </div>
                </div>
                <div className={contentCls}>
                  {(sectionList || []).map((section: any) => {
                    const { anchor } = section;
                    const Com = sectionMap[anchor];
                    return (
                      <Com
                        key={`${anchor}_${locale}`}
                        {...section}
                        data={pageData[anchor as keyof IInvestorRelationData]}
                        lang={stockInfoLang}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          </Page>
        </ScrollPosition>
      </AnchorProvider>
    </>
  );
}

export async function getServerSideProps({ locale, query }: any) {
  const pageData = await getIRPageData(locale);
  const serverData = await serverSideTranslation(locale, ['investor', 'cookie', 'common']);
  const pageContentParams = {
    uri: '/investor-relations',
    pageContentNo: query.pageContentNo,
    preview: query.preview,
  };
  const pageContentData = await getPageContent(pageContentParams, locale);
  return {
    props: {
      lxCustom: {
        cid: 'c_donation_b2eadoie',
        locale,
        ...pageContentData?.shareContent,
      },
      pageContentData,
      pageData,
      ...genShareConfig(pageContentData),
      ...serverData,
    },
  };
}

export default InvestorRelation;
